<template>
  <div class="sticky-container">
    <div
      class="hero is-large is-primary main-hero"
      ref="hero"
      @drop="handleDrop($event)"
      :class="{
        'is-sticky': isSticky,
        'is-processing': isUploadProcessing,
        'is-checkout': isCheckout,
        'is-pulsating': isFileDragging,
      }"
      :style="{ top: (isSticky ? 112 + heightToStickAt : 0) + 'px' }"
    >
      <div
        class="main-hero__image"
        v-if="
          [
            orderProcess.STEPS.NO_ORDER_ONGOING,
            orderProcess.STEPS.ORDER_COMPLETED,
          ].includes(currentOrderProcessStep)
        "
      >
        <figure v-if="heroVideoPath" class="image video" style="position: relative; height: 100%">
          <video
              class="has-ratio"
              autoplay="autoplay"
              muted="muted"
              loop="loop"
              preload="true"
              playsinline
              style="height: 100%; width: 100%; object-fit: cover"
          >
            <source v-bind:src="heroVideoPath"
                    type="video/mp4"/>
          </video>
        </figure>
        <template v-else>
          <img alt="Hero Banner" v-if="backgroundImage" :src="backgroundImage" />
          <img
            alt="Hero Banner"
            v-else
            src="@/assets/img/BannerImage.jpg"
            class="hero__background-image"
          />
        </template>
      </div>
      <div class="hero-body main-hero__body">
        <div class="container py-6 has-text-left" v-if="!isCheckout">
          <div
            v-if="isUploadProcessing"
            key="upload-spinner"
            class="container is-loading"
          ></div>
          <div
            v-if="isUploadProcessing"
            class="is-size-4 mt-3 has-text-centered"
            style="width: 100%"
          >
            {{ fileUploadState.message }}
          </div>
        </div>
      </div>
      <div
        v-show="!isCheckout"
        class="hero-foot main-hero__footer"
        ref="heroFooter"
      >
      <div class="container">
        <div class="columns is-gapless" style="padding: 0 10%;">
          <div class="column is-half">
            <div class="main-hero__cta" v-if="!isUploadProcessing">
              <template v-if="pageSubtitle.length > 0">
                <!--h1 class="title is-1">{{$t('checkout.upload.defaultHeader')}}</h1-->
                <!--h2 class="title is-2">{{ pageSubtitle }}</h2-->
              </template>
              <div>
                <b-button
                  @click="selectFile"
                  type="is-primary-dark is-rounded"
                  is-rounded
                  size="is-large"
                  >{{ $t("checkout.upload.choose3DFile") }}
                </b-button>
              </div>
              <div
                class="divider has-background-white my-5"
                style="width: 100%"
              ></div>
              <div class="mt-3">
                <p class="title is-4 has-text-white" style="text-shadow: none">
                  {{ $t("checkout.upload.dontHaveAFileYet") }}
                </p>
                <a
                  @click="useDemoObject"
                  class="has-text-white is-size-5"
                  style="display: inline-flex; align-items: center"
                >
                  {{ $t("checkout.upload.startDemo") }}
                  <SvgIcon
                    icon="icon--directional_arrow-right"
                    class="is-white ml-3"
                    style="transform: scale(0.7)"
                  />
                </a>
              </div>
              <input
                @change="processFile(null)"
                type="file"
                id="3d-file"
                hidden
                ref="fileInput"
              />
            </div>
          </div>
          <div class="column is-one-half has-background-white"></div>
        </div>
      </div>
        
      </div>
      <div v-if="isUploadProcessing" class="upload-progress">
        <div
          class="mas-progress"
          :style="{ width: uploadProgress + '%' }"
        ></div>
      </div>
    </div>
    <div
      class="sticky-container__placeholder"
      ref="stickyPlaceholder"
      v-if="isSticky"
      :style="{ height: heroHeight + 'px' }"
    ></div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { orderProcess } from "../../store/constants/order-process";
import { eventBus } from "../../events/event-bus";
import i18n from "../../i18n";
import SvgIcon from "../svg/SvgIcon";

export default {
  name: "MainHero",
  components: {
    SvgIcon,
  },
  data() {
    return {
      isSticky: false,
      headerHeight: 112,
      heroFooterHeight: 0,
      heroHeight: 0,
      isUploadProcessing: false,
      orderProcess: orderProcess,
      supportsDragAndDrop: false,
      isFileDragging: false,
      fileUploadState: "",
      uploadProgress: 0,
      heroVideoPath: process.env.VUE_APP_HERO_BANNER_VIDEO_PATH ||
        "https://content.murtfeldt-as.de/fileadmin/redakteure/startseite/Maagtechnic-Banner-Video-Selektives-Lasersintern-Logo.mp4",
    };
  },
  computed: {
    ...mapGetters(["cmsPage", "screenSize"]),
    currentOrderProcessStep: {
      get() {
        return this.$store.getters["currentOrderProcessStep"];
      },
      set(orderProcessStep) {
        this.$store.dispatch("setCurrentOrderProcessStep", orderProcessStep);
      },
    },
    isCheckout() {
      return [orderProcess.STEPS.CONFIGURE_MODEL].includes(
        this.currentOrderProcessStep
      );
    },
    heightToStickAt() {
      return -(this.heroHeight - this.heroFooterHeight);
    },
    backgroundImage() {
      if (this.cmsPage.page && this.cmsPage.page.resources.media.length > 0) {
        return this.cmsPage.page.resources.media[0].publicUrl;
      }
    },
    pageSubtitle() {
      if (this.cmsPage.page) {
        return this.cmsPage.page.meta.subtitle;
      }
      return "";
    },
  },
  methods: {
    setUpEventListeners() {
      let vm = this;
      if (window.FormData && window.FileReader) {
        vm.supportsDragAndDrop = true;

        [
          "drag",
          "dragstart",
          "dragend",
          "dragover",
          "dragenter",
          "dragleave",
          "drop",
        ].forEach((eventType) => {
          window.addEventListener(eventType, (event) => {
            event.preventDefault();
            event.stopPropagation();
          });
        });

        ["dragenter", "dragover"].forEach((eventType) => {
          window.addEventListener(eventType, (event) => {
            vm.isFileDragging = vm.dragEventContainFiles(event);
          });
        });

        ["dragleave", "drop"].forEach((eventType) => {
          window.addEventListener(eventType, (event) => {
            vm.isFileDragging = false;
          });
        });
      }
    },
    dragEventContainFiles(dragEvent) {
      if (dragEvent.dataTransfer.types) {
        for (let i = 0; i < dragEvent.dataTransfer.types.length; i++) {
          if (dragEvent.dataTransfer.types[i] === "Files") {
            return true;
          }
        }
      }

      return false;
    },
    handleDrop(event) {
      event.preventDefault();
      this.processFile(event.dataTransfer.files[0]);
    },
    selectFile() {
      let clickEvent = new MouseEvent("click", {
        view: window,
        bubbles: true,
        cancelable: false,
      });
      this.$refs.fileInput.dispatchEvent(clickEvent);
    },
    processFile(file) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      let vm = this;
      vm.isUploadProcessing = true;
      vm.file = file || this.$refs.fileInput.files[0];

      vm.$store
        .dispatch("upload3DFile", { file: vm.file })
        .then(() => {
          vm.isUploadProcessing = false;
          vm.currentOrderProcessStep = orderProcess.STEPS.CONFIGURE_MODEL;
          vm.$store.dispatch("setCheckoutVisibility", true);
        })
        .catch(() => {
          vm.isUploadProcessing = false;
          vm.currentOrderProcessStep = orderProcess.STEPS.NO_ORDER_ONGOING;
        });
    },
    useDemoObject() {
      window.scrollTo({ top: 0, behavior: "smooth" });
      this.isUploadProcessing = true;
      this.fileUploadState = {
        stateFrom: "uploading",
        stateTo: "analyzing",
        message: this.$i18n.t("checkout.upload.uploadState.analyzing"),
      };
      setTimeout(() => {
        this.$store.dispatch("useDemoObject").then(() => {
          this.isUploadProcessing = false;
          this.currentOrderProcessStep = orderProcess.STEPS.CONFIGURE_MODEL;
          this.$store.dispatch("setCheckoutVisibility", true);
        });
      }, 3000);
    },
    showFileUploadState(message) {
      this.fileUploadState = message;
    },
    evaluateStickyness() {
      let scrollPosition = window.pageYOffset;
      this.adjustHeights();
      this.isSticky = scrollPosition >= -this.heightToStickAt;
    },
    adjustHeights() {
      this.heroHeight = this.$refs.hero.offsetHeight;
      this.heroFooterHeight = this.$refs.heroFooter.offsetHeight;
    },
    attachEventListeners() {
      /** 
      window.addEventListener("scroll", this.evaluateStickyness, {
        passive: true,
      });
      */
      window.addEventListener("resize", this.adjustHeights);
    },
    removeEventListeners() {
      window.removeEventListener("scroll", this.evaluateStickyness);
      window.removeEventListener("resize", this.adjustHeights);
    },
  },
  created() {
    this.attachEventListeners();
    let vm = this;
    eventBus.$on("fileUploadStateChanged", (message) => {
      vm.showFileUploadState(message);
    });
    eventBus.$on("fileUploadProgress", (progress) => {
      vm.uploadProgress = progress;
    });

    eventBus.$on("fileUploadAborted", () => {
      vm.isUploadProcessing = false;
      vm.fileUploadState = "";
      vm.uploadProgress = 0;
    });
  },
  mounted() {
    this.heroHeight = this.$refs.hero.offsetHeight;
    this.heroFooterHeight = this.$refs.heroFooter.offsetHeight;
    this.setUpEventListeners();
  },
  destroyed() {
    this.removeEventListeners();
  },
};
</script>
